import { Fragment, useEffect, useState } from "react"
import image, { PRODUCT_COVER, PRODUCT_COVER_OLD } from "lacinetek-common/src/images"

import { useTranslation } from "lib/i18n/translation"
import { useVideoPlayer } from "lib/video-player"
import { FETCH_TREASURE_LIGHT } from "lib/graphql"
import { BONUS_ARCHIVES, BONUS_EXCLUSIFS } from "lib/tools/product"
import { useMainCategories } from "lib/tools/categories"

import SkeletonLink from "components/common/skeleton-link"
import Query from "components/common/gql-query"
import ProductAccessLink from "components/common/product-access-link"
import ProductAccessAutoplay from "components/common/product-access-autoplay"
import Image from "components/common/image"

import { StructuredData } from "./structured-data"
import { SubscriptionAvailability } from "./subscription-availability"

const Header = ({
	film,
	goTo,
	loading,
	autoplay,
	audioDescription
}) => {
	return (
		<>
			<StructuredData film={film} />
			{film.inTreasure
				? <Query
					query={FETCH_TREASURE_LIGHT}
					options={{ variables: { id: film.inTreasure.id } }}
					renderLoading={() => (
						<TreasureHeader treasure={film.inTreasure} film={film} loading={loading} />
					)}
				>
					{({ cms: { categories: { items: [treasure] } } }) => (
						<TreasureHeader treasure={treasure} film={film} loading={loading} />
					)}
				</Query>
				: <FilmHeader
					film={film}
					loading={loading}
					goTo={goTo}
					autoplay={autoplay}
					audioDescription={audioDescription}
				/>
			}

		</>
	)
}

const TreasureHeader = ({ treasure, film, loading }) => {
	const { c } = useTranslation("common")

	return (
		<>
			<section className={`focus institution ${loading ? "skel-loading" : ""}`}>
				<div className="infos">
					<figure className={loading ? "skeleton" : ""}>
						<picture>
							<Image
								src={image(film, PRODUCT_COVER_OLD)}
								alt={film.name}
							/>
						</picture>
					</figure>

					<div className="content">
						<h1 className={`title skel`}>{film.name}</h1>
						<div className={`film-infos skel`}>
							<span className="director">{c("by")} {film.director}</span>
							<span className="duration">{film.duration}</span>
							<span className="year">{film.year}</span>
							<span className="country">{film.origin}</span>
						</div>
						{
							film.inCollection
							&& <SkeletonLink
								href="/treasures/[id]/[collection]"
								as={`/treasures/${treasure.linkRewrite}/${film.inCollection.linkRewrite}`}
							>
								<a title={c("see-collection")} className="collection-link">
									Collection <strong>{film.inCollection.name}</strong>
								</a>
							</SkeletonLink>
						}
					</div>
				</div>

				<SubscriptionAvailability product={film} loading={loading} />
				<Actions film={film} loading={loading} />
			</section>
			<section>
				<h2 className="section-title icon-eye">{c("proposal-from")} <strong>{treasure.name}</strong></h2>
				{
					(film.descriptionShort && film.descriptionShort !== "")
					&& <div className="columnizer">
						<div className="description">{film.descriptionShort}</div>
					</div>

				}
			</section>
		</>
	)
}

export default Header

const FilmHeader = (
	{ film, loading, goTo, autoplay, audioDescription }
) => {
	const { c } = useTranslation("common")
	const { seeExtract } = useVideoPlayer()
	const { allLists } = useMainCategories()

	const recommenders = film.recommendersIds
		.map((id, index) => allLists.find(l => l.director.id == id))
		.filter(l => !!l)

	return (
		<>
			<section className={`focus film ${loading ? "skel-loading" : ""}`}>
				<div className="infos">
					<h1 className="title skel">{film.name}</h1>
					<span className="original skel">{film.original_title}</span>
					<div className="film-infos">
						<span className="director skel">{c("by")} {film.director}</span>
						<span className="duration skel">{film.duration}</span>
						<span className="year skel">{film.year}</span>
						<span className="country skel">{film.origin}</span>
					</div>
				</div>
				<SubscriptionAvailability product={film} loading={loading} />
				{
					(autoplay && !loading)
					&& <ProductAccessAutoplay
						product={film}
						audioDescription={audioDescription}
					/>
				}
				<Actions film={film} loading={loading} />
				<figure className="background skel">
					<picture>
						<Image
							src={image(film, PRODUCT_COVER)}
							alt={film.name}
						/>
					</picture>
				</figure>
			</section>
			<section className={`short ${loading ? "skel-loading" : ""}`}>
				<p className={`is-recommended ${loading ? "skeleton" : ""}`}>
					{
						film.recommendersIds.length > 0
						&& (
							<>
								<b>{c("recommended-by")}</b>
								{
									recommenders.map((list, index) => (
										<Fragment key={`recommender-${index}`}>
											<SkeletonLink href={`/lists/[id]`} as={`/lists/${c(list.linkRewrite)}`} >
												<a title={c("see-list")}>{list.name.replace(" | ", " ")}</a>
											</SkeletonLink>
											{index < recommenders.length - 1 ? ", " : ""}
										</Fragment>
									))
								}
							</>
						)
					}
				</p>
			</section>
			<section>
				<nav className={`sum-up skel`}>
					<a
						href="#"
						rel="nofollow noopener"
						title={c("watch-extract")}
						className={film.trailersCount > 0 || film.extractsCount > 0 ? "" : "disabled"}
						onClick={e => { e.preventDefault(); seeExtract(film) }}
					>
						<strong>{film.extractsCount || film.trailersCount}</strong> {film.extractsCount > 0 ? c("extract") : c("teaser")}
					</a>
					<a
						href="#section-bonus-exclusive"
						onClick={() => goTo(BONUS_EXCLUSIFS)}
						title={c("watch-exclusifs-bonus")}
						rel="nofollow noopener"
						className={film.bonusesExclusifsCount > 0 ? "" : "disabled"}
					>
						<strong>{film.bonusesExclusifsCount}</strong> {c("bonus-exclusifs-title")}
					</a>
					<a
						href="#section-bonus-archives"
						onClick={() => goTo(BONUS_ARCHIVES)}
						title={c("watch-archives-bonus")}
						rel="nofollow noopener"
						className={film.bonusesArchivesCount > 0 ? "" : "disabled"}
					>
						<strong>{film.bonusesArchivesCount}</strong> {c("bonus-archives-title")}
					</a>
					<a
						href="#section-gallery"
						rel="nofollow noopener"
						title={c("see-gallery")}
						className={film.images.length ? "" : "disabled"}
					>
						<strong>1</strong> {c("images")}
					</a>
				</nav>
			</section>
		</>
	)
}

const Actions = ({ film, loading }) => {
	const [displayButton, setDisplayButton] = useState(false)

	useEffect(() => {
		setDisplayButton(true)
	}, [])

	return (
		<nav className={`focus-nav`}>
			{!loading && displayButton && <ProductAccessLink product={film} alt canDownload />}
		</nav>
	)
}
